import { useState } from 'react';
import {
  Container,
  Title,
  Stack,
  Text,
  Paper,
  TextInput,
  Button,
  Group,
  Divider,
  Avatar,
  PasswordInput,
  Alert,
  Modal,
} from '@mantine/core';
import {
  IconUser,
  IconMail,
  IconDeviceFloppy,
  IconLock,
  IconAlertCircle,
  IconCalendar,
} from '@tabler/icons-react';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/hooks/useOrganization';
import { supabase } from '@/lib/supabase';
import { getInitials } from '@/lib/utils';
import { notifications } from '@mantine/notifications';

export function ProfilePage() {
  const { user } = useAuth();
  const { organization, membership } = useOrganization();
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [passwordModalOpened, setPasswordModalOpened] = useState(false);

  // Profile form state
  const [fullName, setFullName] = useState(user?.user_metadata?.full_name || '');
  const [hasProfileChanges, setHasProfileChanges] = useState(false);

  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const userInitials = getInitials(user?.user_metadata?.full_name || user?.email);

  const handleNameChange = (value: string) => {
    setFullName(value);
    setHasProfileChanges(value !== user?.user_metadata?.full_name);
  };

  const handleSaveProfile = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        data: { full_name: fullName },
      });

      if (error) throw error;

      notifications.show({
        title: 'Success',
        message: 'Profile updated successfully',
        color: 'green',
      });

      setHasProfileChanges(false);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update profile',
        color: 'red',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetProfile = () => {
    setFullName(user?.user_metadata?.full_name || '');
    setHasProfileChanges(false);
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      notifications.show({
        title: 'Error',
        message: 'New passwords do not match',
        color: 'red',
      });
      return;
    }

    if (newPassword.length < 6) {
      notifications.show({
        title: 'Error',
        message: 'Password must be at least 6 characters long',
        color: 'red',
      });
      return;
    }

    setIsPasswordLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      notifications.show({
        title: 'Success',
        message: 'Password changed successfully',
        color: 'green',
      });

      // Clear password fields and close modal
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordModalOpened(false);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to change password',
        color: 'red',
      });
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const getRoleDisplay = () => {
    if (!membership) return 'Member';
    if (membership.is_admin) return 'Admin';
    return membership.role?.charAt(0).toUpperCase() + membership.role?.slice(1) || 'Member';
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Container size="md">
      <Stack gap="lg">
        <div>
          <Title order={1}>Profile</Title>
          <Text c="dimmed" size="sm">
            Manage your personal information and account settings
          </Text>
        </div>

        {/* Profile Information */}
        <Paper withBorder p="md">
          <Stack gap="md">
            <Group>
              <Avatar color="blue" size="lg" radius="xl">
                {userInitials}
              </Avatar>
              <div>
                <Text fw={600} size="lg">
                  {user?.user_metadata?.full_name || user?.email?.split('@')[0]}
                </Text>
                {organization && (
                  <Text size="sm" c="dimmed">
                    {organization.name} • {getRoleDisplay()}
                  </Text>
                )}
              </div>
            </Group>

            <Divider />

            <TextInput
              label="Full Name"
              placeholder="Enter your full name"
              value={fullName}
              onChange={(e) => handleNameChange(e.currentTarget.value)}
              leftSection={<IconUser size={16} />}
            />

            <Group grow>
              <TextInput
                label="Email"
                value={user?.email || ''}
                readOnly
                disabled
                leftSection={<IconMail size={16} />}
              />

              <TextInput
                label="Account Created"
                value={formatDate(user?.created_at)}
                readOnly
                disabled
                leftSection={<IconCalendar size={16} />}
              />
            </Group>

            {hasProfileChanges && (
              <Group justify="flex-end" mt="md">
                <Button variant="subtle" onClick={handleResetProfile}>
                  Reset
                </Button>
                <Button
                  leftSection={<IconDeviceFloppy size={16} />}
                  onClick={handleSaveProfile}
                  loading={isLoading}
                >
                  Save Changes
                </Button>
              </Group>
            )}
          </Stack>
        </Paper>

        {/* Change Password Section */}
        <Paper withBorder p="md">
          <Stack gap="md">
            <div>
              <Text fw={600} size="lg" mb="xs">
                Password & Security
              </Text>
              <Text c="dimmed" size="sm">
                Manage your password and security settings
              </Text>
            </div>

            <Divider />

            <Button
              leftSection={<IconLock size={16} />}
              onClick={() => setPasswordModalOpened(true)}
              variant="light"
            >
              Change Password
            </Button>
          </Stack>
        </Paper>
      </Stack>

      {/* Password Change Modal */}
      <Modal
        opened={passwordModalOpened}
        onClose={() => {
          setPasswordModalOpened(false);
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        }}
        title="Change Password"
        size="md"
      >
        <Stack gap="md">
          <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light">
            Password must be at least 6 characters long
          </Alert>

          <PasswordInput
            label="New Password"
            placeholder="Enter new password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.currentTarget.value)}
            leftSection={<IconLock size={16} />}
          />

          <PasswordInput
            label="Confirm New Password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.currentTarget.value)}
            leftSection={<IconLock size={16} />}
          />

          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => {
                setPasswordModalOpened(false);
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
              }}
            >
              Cancel
            </Button>
            <Button
              leftSection={<IconLock size={16} />}
              onClick={handleChangePassword}
              loading={isPasswordLoading}
              disabled={!newPassword || !confirmPassword}
            >
              Change Password
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
